import { formatters, useTranslation } from "@equiem/web-ng-lib";
import { Button, EscapedHtmlDiv, IframelyContext, ProgressCircle, Site, useShowError, Viewer } from "@equiem/web-ng-lib";
import { escape } from "html-escaper";
import React, { MouseEvent, useContext, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { BsReplyFill } from "react-icons/bs";
import scrollIntoView from "scroll-into-view";
import { CommentsForContentQuery, useDeleteCommentMutation } from "../../generated/gateway-client";
import { ContentType } from "../../lib/ContentType";
import { DateTime } from "../../lib/DateTime";
import { CommentAvatar } from "./CommentAvatar";
import { CommentForm } from "./CommentForm";
import { updateCommentCount } from "./updateCommentCount";

type RequiredComment = NonNullable<
  NonNullable<
    CommentsForContentQuery["commentsForContent"]["edges"][number]
  >["node"]
>;
type Comment = Omit<RequiredComment, "comments"> & { comments?: RequiredComment["comments"] };

export interface Props {
  comment: Comment;
  contentUuid: string;
  contentType: ContentType;
  parent?: Comment;
  onDelete?: (args: { comment: Comment }) => Promise<void>;
}

export const Comment: React.FC<Props> = ({
  comment,
  contentUuid,
  contentType,
  parent,
  onDelete = async () => undefined,
}) => {
  const { t, i18n } = useTranslation();
  const { timezone } = useContext(Site);
  const viewer = useContext(Viewer);
  const iframely = useContext(IframelyContext);
  const showError = useShowError();
  const [replying, setReplying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const created = DateTime.fromMillis(comment.created, { zone: timezone });
  const [deleteComment, { loading: deleting, client }] = useDeleteCommentMutation();
  const replyInputRef = useRef<HTMLTextAreaElement>();

  const focusReplyInput = () => {
    if (replyInputRef.current != null) {
      scrollIntoView(replyInputRef.current, { time: 200 }, () => {
        replyInputRef.current?.focus();
      });
    }
  };

  const handleReply = async (e: MouseEvent) => {
    e.preventDefault();
    setReplying(true);
    focusReplyInput();
  };

  const handleDelete = async (e: MouseEvent) => {
    e.preventDefault();
    setConfirmDelete(true);
  };

  const doDelete = async () => {
    try {
      await deleteComment({
        variables: { uuid: comment.uuid },
      });

      if (client != null) {
        updateCommentCount({ contentType, contentUuid, client });
      }

      await onDelete({ comment });
    }
    catch (e) {
      showError(e);
    }
  };

  useEffect(() => {
    if (replying) {
      focusReplyInput();
    }
  }, [replying])

  const replies = comment.comments ?? [];
  const ownComment = viewer?.profile?.uuid === comment.author?.uuid;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  let countReplacements = 0;
  const body = escape(comment.body).replace(urlRegex, (url) => {
    countReplacements++;

    return `<a data-iframely-url href="${url}">${url}</a>`;
  });

  useEffect(() => {
    if (countReplacements > 0) {
      iframely.load();
    }
  });

  return (
    <div className={`comment my-4 ${deleting ? "deleting" : ""}`}>
      <div className="d-flex">
        <div className="pr-2 pr-md-3">
          <CommentAvatar avatar={comment.author?.avatar} />
        </div>
        <div className="w-100">
          <div className="bg-white p-2 p-md-3 rounded">
            <div className="mb-2">
              <address className="d-inline-block m-0 mr-2">
                <span className="font-weight-bold">
                  {comment.author?.displayName}
                </span>
              </address>
              <time
                className="d-inline-block text-muted"
                title={`${formatters.dateshort(comment.created, i18n.language, { timezone })} ${formatters.timeshort(comment.created, i18n.language, { timezone })}`}
                dateTime={created.toISO()}
              >
                {formatters.dateshort(comment.created, i18n.language, { timezone })}
              </time>
            </div>
            <EscapedHtmlDiv className="body" html={body} />
          </div>
          <div className="actions mt-2 mb-1 pl-2 pl-md-3 font-weight-bold">
            {parent == null ? (
              <a href="#" className="mr-2" onClick={handleReply}>
                <BsReplyFill size="14" /> {t("common.reply")}
              </a>
            ) : null}
            {ownComment ? (
              <a href="#" onClick={handleDelete} className="mr-2">
                {t("common.delete")}
              </a>
            ) : null}
            {deleting ? <ProgressCircle size="sm" /> : null}
          </div>

          {replies.length > 0 || replying ? (
            <div className="replies">
              {replies.length > 0
                ? replies.map((reply) => (
                  <Comment
                    contentType={contentType}
                    contentUuid={contentUuid}
                    parent={comment}
                    onDelete={onDelete}
                    key={reply.uuid}
                    comment={reply}
                  />
                ))
                : null}
              {replying ? (
                <CommentForm
                  contentType={contentType}
                  contentUuid={contentUuid}
                  parentUuid={comment.uuid}
                  cancelable={true}
                  bodyRef={replyInputRef}
                  className="my-4"
                  onAddComment={async () => {
                    setReplying(false);
                  }}
                  onCancel={async () => {
                    setReplying(false);
                  }}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <Modal
        show={confirmDelete}
        keyboard={true}
        centered={true}
        onHide={() => {
          setConfirmDelete(false);
        }}
      >
        <Modal.Header>
          <Modal.Title>{t("main.deleteComment")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("main.confirmationDeleteComment")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline"
            onClick={() => {
              setConfirmDelete(false);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="solid"
            onClick={async () => {
              setConfirmDelete(false);
              await doDelete();
            }}
          >
            {t("common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      <style jsx>{`
        .comment {
          font-size: 0.875rem;
        }
        .comment address, .comment time {
          font-size:0.75rem;
        }
        .body {
          white-space: pre-wrap;
        }
        .deleting {
          opacity: 0.3;
          pointer-events: none;
        }
        .comment :global(.iframely-embed) {
          max-width: 600px;
        }
      `}</style>
    </div>
  );
};
